import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  deleteDoc,
  doc,
} from "firebase/firestore/lite";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseApp } from "../firebase/firebaseApp";
import { useAuth } from "../context/AuthContext";
import { useDateTimeFormat } from "../hooks/useDateTimeFormat";

// Define Super Admin email locally for immediate front-end checks
const SUPER_ADMIN_EMAIL = "ben@benwjd.com".toLowerCase();

// Updated ROLE_OPTIONS to include 'none' (Participant)
const ROLE_OPTIONS = [
  { value: "none", label: "Participant (No Role)" },
  { value: "clinician", label: "Clinician / Site Staff" },
  { value: "admin", label: "Admin" },
];

export default function AdminPage() {
  const { claims, user } = useAuth();
  const { formatDate } = useDateTimeFormat();
  const db = getFirestore(firebaseApp);
  const functions = getFunctions(firebaseApp, "europe-west2");

  // --- API Call Stabilization ---
  const createInviteAPI = httpsCallable(functions, "createInvite");
  const getUserByEmailAPI = httpsCallable(functions, "getUserByEmail");
  const updateUserRoleAPI = httpsCallable(functions, "updateUserRole");
  const getClinicUsersAPI = httpsCallable(functions, "getClinicUsers");

  const createInviteFn = useCallback(
    (data) => createInviteAPI(data),
    [createInviteAPI]
  );
  const getUserByEmailFn = useCallback(
    (data) => getUserByEmailAPI(data),
    [getUserByEmailAPI]
  );
  const updateUserRoleFn = useCallback(
    (data) => updateUserRoleAPI(data),
    [updateUserRoleAPI]
  );
  const getClinicUsersFn = useCallback(
    () => getClinicUsersAPI(),
    [getClinicUsersAPI]
  );
  // --- END API Call Stabilization ---

  // State
  const [activeTab, setActiveTab] = useState("invites"); // 'invites' | 'users'
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState("");

  // Invite Form State
  const [inviteForm, setInviteForm] = useState({
    email: "",
    role: "clinician",
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMsg, setInviteMsg] = useState("");

  // User Edit State
  const [searchEmail, setSearchEmail] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("none");
  const [userLoading, setUserLoading] = useState(false);
  const [userMsg, setUserMsg] = useState("");

  // User List State
  const [clinicUsers, setClinicUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  // NEW STATE: Guard against repeated fetching on failure
  const [hasUserListFetchError, setHasUserListFetchError] = useState(false);

  // Custom memoized flags for permissions
  const { isSuperAdminByEmail, isAdmin, effectiveRole } = useMemo(() => {
    const userEmail = (user?.email || "").toLowerCase();
    const isSA = userEmail === SUPER_ADMIN_EMAIL;

    // Determine the effective role
    let role = claims?.role || "participant";

    // If email matches AND no specific claims are set yet, treat as super_admin.
    if (isSA && role === "participant") {
      role = "super_admin";
    }

    const isAdm = role === "admin" || role === "super_admin";

    return {
      isSuperAdminByEmail: isSA,
      isAdmin: isAdm,
      effectiveRole: role,
    };
  }, [claims, user?.email]);

  const loadInvites = useCallback(async () => {
    setInvitesLoading(true);
    setInvitesError("");

    try {
      const snap = await getDocs(collection(db, "invites"));
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => {
        const tA = a.createdAt?.toMillis?.() || 0;
        const tB = b.createdAt?.toMillis?.() || 0;
        return tB - tA;
      });
      setInvites(items);
    } catch (error) {
      console.error("Failed to load invites", error);
      setInvites([]);
      setInvitesError("Failed to load invites. Please try again.");
    } finally {
      setInvitesLoading(false);
    }
  }, [db]);

  // 1. Load invites on demand (no Firestore realtime listener)
  useEffect(() => {
    if (activeTab !== "invites" || !isAdmin) {
      return;
    }

    loadInvites();
  }, [activeTab, isAdmin, loadInvites]);

  // 2. Fetch Users when Tab Changes (Stabilized Dependency + Error Guard)
  useEffect(() => {
    if (activeTab === "users" && isAdmin && !hasUserListFetchError) {
      const fetchUsers = async () => {
        setListLoading(true);
        try {
          const res = await getClinicUsersFn();
          setClinicUsers(res.data);
          console.log(res.data);
          // If successful, reset error flag just in case
          setHasUserListFetchError(false);
        } catch (err) {
          console.error("Failed to load users list", err);
          // Crucial: Set error flag to true to prevent future automatic re-runs
          setHasUserListFetchError(true);
        } finally {
          setListLoading(false);
        }
      };
      fetchUsers();
    }
  }, [activeTab, isAdmin, hasUserListFetchError]); // Depend on error state

  // 3. Invite Logic
  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteMsg("");
    try {
      const res = await createInviteFn({
        email: inviteForm.email,
        role: inviteForm.role === "none" ? undefined : inviteForm.role,
        expiryDays: 7,
      });
      setInviteMsg(`Success! Code: ${res.data.code}`);
      setInviteForm({ email: "", role: "clinician" });
      await loadInvites();
    } catch (err) {
      console.error(err);
      setInviteMsg(`Error: ${err.message}`);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeleteInvite = async (id) => {
    // Console message instead of alert/confirm
    console.warn(
      "Attempting to revoke invite. Please confirm deletion intent in console."
    );

    try {
      await deleteDoc(doc(db, "invites", id));
      console.log(`Invite ${id} revoked successfully.`);
      await loadInvites();
    } catch (error) {
      console.error("Failed to revoke invite:", error);
    }
  };

  // 4. User Logic
  const handleSearchUser = async (e) => {
    e.preventDefault();
    loadUserForEdit(searchEmail);
  };

  const loadUserForEdit = async (email) => {
    setUserLoading(true);
    setFoundUser(null);
    setSelectedRole("none");
    setUserMsg("");
    try {
      const res = await getUserByEmailFn({ email: email });
      setFoundUser(res.data);
      setSelectedRole(res.data.claims?.role || "none");
    } catch (err) {
      console.error("Search User Error:", err);
      // Removed specific 'different clinic' error message
      if (err.message.includes("not-found")) {
        setUserMsg("User not found in Auth database.");
      } else {
        setUserMsg(`Error: ${err.message}`);
      }
    } finally {
      setUserLoading(false);
    }
  };

  // Clicking a row in the list
  const handleSelectUserFromList = (user) => {
    setSearchEmail(user.email);
    loadUserForEdit(user.email);
  };

  const handleUpdateRole = async () => {
    if (!foundUser || !selectedRole) return;

    setUserLoading(true);

    // Convert 'none' selection to null for Firebase claims removal
    const roleToSet = selectedRole === "none" ? null : selectedRole;

    try {
      await updateUserRoleFn({
        targetUid: foundUser.uid,
        newRole: roleToSet, // null will remove the role claim
      });

      setFoundUser((prev) => ({
        ...prev,
        claims: { ...prev.claims, role: roleToSet },
      }));
      setSelectedRole(roleToSet || "none");
      setUserMsg("Role updated successfully.");

      // If the list previously failed due to a transient error, try reloading it
      if (hasUserListFetchError) setHasUserListFetchError(false);

      // Refresh the user list for real-time display
      const res = await getClinicUsersFn();
      setClinicUsers(res.data);
    } catch (err) {
      console.error(err);
      setUserMsg(`Update failed: ${err.message}`);
    } finally {
      setUserLoading(false);
    }
  };

  return (
    <div className="w-full h-full p-6 overflow-y-auto mx-auto ">
      <div className="flex flex-col gap-4 mb-6">
        <h1 className="supertitle-text">Admin Dashboard</h1>
        <p className="muted-text">
          <span className="font-bold capitalize">Role: {effectiveRole}</span>
        </p>
        <p className="text-sm text-gray-600">
          **Super Admin Email:** {SUPER_ADMIN_EMAIL}. This account has global
          permissions.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b mb-6">
        <button
          onClick={() => setActiveTab("invites")}
          className={`pb-2 px-4 font-semibold ${
            activeTab === "invites"
              ? "border-b-2 border-primary text-primary"
              : "text-gray-500"
          }`}
        >
          Manage Invites
        </button>
        <button
          onClick={() => setActiveTab("users")}
          className={`pb-2 px-4 font-semibold ${
            activeTab === "users"
              ? "border-b-2 border-primary text-primary"
              : "text-gray-500"
          }`}
        >
          Manage Users & Roles
        </button>
      </div>

      {!isAdmin && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Permission Denied!</strong>
          <span className="block sm:inline">
            {" "}
            You must be an Admin or Super Admin to use this management tool.
          </span>
        </div>
      )}

      {/* --- INVITES TAB --- */}
      {activeTab === "invites" && isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-1 bg-gray-50 p-4 rounded shadow-sm h-fit">
            <h3 className="font-semibold mb-4">Create New Invite</h3>
            <form onSubmit={handleCreateInvite} className="flex flex-col gap-3">
              <input
                type="email"
                placeholder="Email Address"
                className="custom-input bg-white"
                required
                value={inviteForm.email}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, email: e.target.value })
                }
              />
              <select
                className="custom-input bg-white"
                value={inviteForm.role}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, role: e.target.value })
                }
              >
                {/* Only allow clinician/admin to be invited */}
                {ROLE_OPTIONS.filter((opt) => opt.value !== "none").map(
                  (opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  )
                )}
              </select>
              <button
                type="submit"
                disabled={inviteLoading}
                className="primary-button w-full"
              >
                {inviteLoading ? "Generating..." : "Generate Code"}
              </button>
              {inviteMsg && (
                <p className="text-xs font-semibold text-blue-600 mt-2 break-words">
                  {inviteMsg}
                </p>
              )}
            </form>
          </div>

          <div className="md:col-span-2">
            <h3 className="font-semibold mb-4">Active & Recent Invites</h3>
            {invitesError ? (
              <p className="mb-3 text-sm font-medium text-red-600">
                {invitesError}
              </p>
            ) : null}
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invitesLoading ? (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-gray-500">
                        Loading invites...
                      </td>
                    </tr>
                  ) : invites.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-gray-500">
                        No invites found.
                      </td>
                    </tr>
                  ) : null}
                  {invites.map((inv) => (
                    <tr key={inv.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono font-bold text-primary">
                        {inv.id}
                      </td>
                      <td className="px-4 py-3">{inv.email}</td>
                      <td className="px-4 py-3 capitalize">
                        {inv.role?.replace("_", " ") || "participant"}
                      </td>
                      <td className="px-4 py-3">
                        {inv.used ? (
                          <span className="text-green-600 bg-green-100 px-2 py-1 rounded text-xs">
                            Used
                          </span>
                        ) : inv.expiresAt?.toMillis() < Date.now() ? (
                          <span className="text-red-600 bg-red-100 px-2 py-1 rounded text-xs">
                            Expired
                          </span>
                        ) : (
                          <span className="text-blue-600 bg-blue-100 px-2 py-1 rounded text-xs">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!inv.used && (
                          <button
                            onClick={() => handleDeleteInvite(inv.id)}
                            className="text-red-600 hover:underline text-xs"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* --- USERS TAB --- */}
      {activeTab === "users" && isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: List of Users */}
          <div>
            <h3 className="font-semibold mb-4">All Users in Project</h3>
            {listLoading ? (
              <p className="text-sm text-gray-500">Loading users...</p>
            ) : (
              <div className="overflow-x-auto border rounded max-h-96 overflow-y-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-100 text-gray-700 uppercase text-xs sticky top-0">
                    <tr>
                      <th className="px-4 py-3">Email</th>
                      {isSuperAdminByEmail && (
                        <th className="px-4 py-3">Clinic</th>
                      )}
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Last Sign In</th>
                      <th className="px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clinicUsers.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="p-4 text-center text-gray-500"
                        >
                          {hasUserListFetchError
                            ? "Failed to load user list. See console for details."
                            : "No users found."}
                        </td>
                      </tr>
                    )}
                    {clinicUsers.map((u) => (
                      <tr
                        key={u.uid}
                        className={`border-b cursor-pointer hover:bg-blue-50 ${
                          foundUser?.uid === u.uid ? "bg-blue-100" : ""
                        }`}
                        onClick={() => handleSelectUserFromList(u)}
                      >
                        <td className="px-4 py-3 font-medium">{u.email}</td>
                        {isSuperAdminByEmail && (
                          <td className="px-4 py-3 text-xs">
                            {u.clinic || "N/A"}
                          </td>
                        )}
                        <td className="px-4 py-3 capitalize">
                          {u.role?.replace("_", " ") || "participant"}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {u.lastSignIn ? formatDate(u.lastSignIn) : "N/A"}
                        </td>
                        <td className="px-4 py-3">
                          <button className="text-primary font-bold text-xs">
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/*  Right: Edit Form  */}
          <div>
            <div className="bg-blue-50 p-4 rounded mb-6 text-sm text-blue-800">
              <p className="font-bold mb-1">Manage Permissions</p>
              Select a user from the list or search manually to edit their role.
              The default role is **Participant**.
            </div>

            {/* Manual Retry Button for Failed Fetch */}
            {hasUserListFetchError && (
              <div className="mb-4">
                <p className="text-red-600 text-sm font-semibold mb-2">
                  Automatic list loading failed.
                </p>
                <button
                  onClick={() => setHasUserListFetchError(false)} // Clear error state to trigger fetch
                  className="primary-button text-sm"
                >
                  Retry Load All Users
                </button>
              </div>
            )}

            <form onSubmit={handleSearchUser} className="flex gap-2 mb-6">
              <input
                type="email"
                placeholder="Or search email manually..."
                className="custom-input flex-1"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                required
              />
              <button
                type="submit"
                disabled={userLoading}
                className="primary-button"
              >
                {userLoading ? "Loading..." : "Search"}
              </button>
            </form>

            {userMsg && (
              <p
                className={`mb-4 text-sm font-bold ${
                  userMsg.includes("success") || userMsg.includes("Success")
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {userMsg}
              </p>
            )}

            {foundUser && (
              <div className="border rounded p-6 bg-white shadow-sm animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold">{foundUser.email}</h3>
                    <p className="text-xs text-gray-500">
                      UID: {foundUser.uid.slice(0, 8)}...
                    </p>
                  </div>
                  {/* Show Clinic only if available or Super Admin */}
                  {(foundUser.claims.clinic || isSuperAdminByEmail) && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {foundUser.claims.clinic
                          ? "Assigned Clinic"
                          : "Participant Status"}
                      </p>
                      <p className="font-semibold text-primary">
                        {foundUser.claims.clinic || "N/A"}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold">Assign Role</label>
                  <div className="flex gap-2 items-center">
                    <select
                      className="custom-input flex-1"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      disabled={userLoading}
                    >
                      {ROLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleUpdateRole}
                      disabled={userLoading}
                      className="primary-button text-sm whitespace-nowrap"
                    >
                      {userLoading ? "Updating..." : "Confirm Change"}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Updates apply immediately. Selecting "Participant (No Role)"
                    will clear the user's role claim.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
